import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { Calendar, MessageCircle, ArrowRight, ShieldAlert, Stethoscope } from "lucide-react";
import { PageShell } from "@/components/PageShell";
import { Breadcrumbs, breadcrumbJsonLd } from "@/components/Breadcrumbs";
import { ExerciseCard } from "@/components/blog/ExerciseCard";
import { FaqAccordion } from "@/components/blog/FaqAccordion";
import { ArticleCard } from "@/components/blog/ArticleCard";
import { BLOG_BY_SLUG, type BlogPost } from "@/lib/blog-posts";
import { categorySlug, relatedPostsFor } from "@/lib/blog-hub";
import { SERVICES_BY_SLUG } from "@/lib/services";
import { SITE } from "@/lib/site";

const BASE = "https://nsphysiotherapy.co.za";

export const Route = createFileRoute("/blog/$slug")({
  loader: ({ params }) => {
    const post = BLOG_BY_SLUG[params.slug];
    if (!post) throw notFound();
    return { post };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) {
      return { meta: [{ title: "Article not found" }, { name: "robots", content: "noindex" }] };
    }
    const p = loaderData.post;
    const url = `${BASE}/blog/${params.slug}`;
    const scripts: { type: string; children: string }[] = [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: p.title,
          description: p.metaDescription,
          datePublished: p.publishedAt,
          dateModified: p.updatedAt ?? p.publishedAt,
          author: {
            "@type": "Person",
            name: "Nhlamulo Khosa",
            jobTitle: "Physiotherapist, BSc Physiotherapy (UCT)",
            url: BASE,
          },
          publisher: { "@type": "Organization", name: "N S Physiotherapy", url: BASE },
          mainEntityOfPage: url,
        }),
      },
    ];

    if (p.faqs && p.faqs.length > 0) {
      scripts.push({
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: p.faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      });
    }

    if (p.exercises && p.exercises.length > 0) {
      scripts.push({
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "HowTo",
          name: `${p.title}: exercise programme`,
          description: p.quickAnswer ?? p.metaDescription,
          step: p.exercises.map((e) => ({
            "@type": "HowToStep",
            name: e.name,
            text: [e.startingPosition, ...e.steps].join(" "),
          })),
        }),
      });
    }

    scripts.push({
      type: "application/ld+json",
      children: JSON.stringify(
        breadcrumbJsonLd([
          { name: "Home", url: `${BASE}/` },
          { name: "Blog", url: `${BASE}/blog` },
          { name: p.title, url },
        ]),
      ),
    });

    return {
      meta: [
        { title: p.metaTitle },
        { name: "description", content: p.metaDescription },
        { property: "og:title", content: p.metaTitle },
        { property: "og:description", content: p.metaDescription },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
        { property: "article:published_time", content: p.publishedAt },
        { property: "article:modified_time", content: p.updatedAt ?? p.publishedAt },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts,
    };
  },
  component: BlogPostPage,
  notFoundComponent: () => (
    <PageShell>
      <div className="container-page py-24 text-center">
        <h1 className="font-display text-3xl font-bold text-primary">Article not found</h1>
        <Link to="/blog" className="btn-hero mt-6 inline-flex h-11 items-center rounded-full px-6 font-semibold">
          Back to blog
        </Link>
      </div>
    </PageShell>
  ),
});

const fmt = (d: string) =>
  new Date(d).toLocaleDateString("en-ZA", { year: "numeric", month: "long", day: "numeric" });

function BlogPostPage() {
  const { post: p } = Route.useLoaderData() as { post: BlogPost };
  const related = relatedPostsFor(p, 3);
  const relatedServices = (p.relatedServices ?? []).map((slug) => SERVICES_BY_SLUG[slug]).filter(Boolean);
  const catSlug = categorySlug(p.category);
  const wa = `https://wa.me/${SITE.whatsappIntl}?text=${encodeURIComponent(
    p.whatsAppMessage ??
      `Hi, I read your article on ${p.title.toLowerCase()} and would like to book a physio appointment.`,
  )}`;

  return (
    <PageShell>
      <Breadcrumbs
        items={[
          { label: "Home", to: "/" },
          { label: "Blog", to: "/blog" },
          { label: p.title },
        ]}
      />
      <article className="container-page max-w-3xl py-10">
        <Link
          to="/blog/category/$slug"
          params={{ slug: catSlug }}
          className="text-xs font-semibold uppercase tracking-widest text-primary/60 hover:text-primary"
        >
          {p.category}
        </Link>
        <h1 className="mt-2 font-display text-3xl font-bold text-primary md:text-4xl">{p.title}</h1>

        <div className="mt-3 rounded-2xl border border-border bg-card p-4 text-sm">
          <div className="flex items-start gap-3">
            <Stethoscope className="mt-0.5 h-5 w-5 shrink-0 text-primary/70" />
            <div>
              <div className="font-semibold text-primary">Nhlamulo Khosa, BSc Physiotherapy (UCT)</div>
              <div className="text-xs text-muted-foreground">
                HPCSA registered physiotherapist, N S Physiotherapy, Eco Park, Centurion
              </div>
              <div className="mt-1 text-xs text-muted-foreground">
                Published {fmt(p.publishedAt)}
                {p.updatedAt ? ` · Last reviewed ${fmt(p.updatedAt)}` : ""}
              </div>
            </div>
          </div>
        </div>

        {p.quickAnswer && (
          <div className="mt-6 rounded-2xl border border-primary/20 bg-secondary/40 p-5">
            <div className="text-xs font-semibold uppercase tracking-widest text-primary/70">Quick answer</div>
            <p className="mt-2 text-foreground/85">{p.quickAnswer}</p>
          </div>
        )}

        <div className="prose prose-slate mt-8 max-w-none">
          <p className="text-lg leading-relaxed text-foreground/85">{p.intro}</p>
          {p.sections?.map((s) => (
            <div key={s.heading}>
              <h2 className="mt-8 font-display text-2xl font-bold text-primary">{s.heading}</h2>
              <p className="mt-3 leading-relaxed text-foreground/80">{s.body}</p>
            </div>
          ))}
        </div>

        {p.exercises && p.exercises.length > 0 && (
          <section className="mt-12">
            <h2 className="font-display text-2xl font-bold text-primary">Exercise programme</h2>
            <p className="mt-2 text-sm text-foreground/70">
              Work within a comfortable range. Mild discomfort is common, sharp or increasing pain is not.
            </p>
            <div className="mt-5 grid gap-4">
              {p.exercises.map((e, i) => (
                <ExerciseCard key={e.name} exercise={e} index={i + 1} />
              ))}
            </div>
          </section>
        )}

        {p.whenToStop && p.whenToStop.length > 0 && (
          <section className="mt-10 rounded-2xl border border-border bg-card p-6">
            <h2 className="flex items-center gap-2 font-display text-xl font-bold text-primary">
              <ShieldAlert className="h-5 w-5 text-primary/70" /> When to stop and get assessed
            </h2>
            <ul className="mt-3 space-y-2 text-sm text-foreground/80">
              {p.whenToStop.map((w) => (
                <li key={w} className="flex gap-2">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary/60" />
                  <span>{w}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {p.whenToSeePhysio && (
          <section className="mt-6 rounded-2xl border border-border bg-secondary/30 p-6">
            <h2 className="font-display text-lg font-bold text-primary">When to see a physiotherapist</h2>
            <p className="mt-2 text-sm text-foreground/80">{p.whenToSeePhysio}</p>
          </section>
        )}

        {relatedServices.length > 0 && (
          <div className="mt-10 rounded-2xl border border-border bg-card p-6">
            <h2 className="font-display text-lg font-bold text-primary">Related physiotherapy services</h2>
            <ul className="mt-3 grid gap-2 sm:grid-cols-2">
              {relatedServices.map((s) => (
                <li key={s.slug}>
                  <Link
                    to="/services/$slug"
                    params={{ slug: s.slug }}
                    className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"
                  >
                    {s.title} <ArrowRight className="h-3 w-3" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}

        {p.faqs && p.faqs.length > 0 && (
          <section className="mt-10">
            <h2 className="font-display text-2xl font-bold text-primary">Frequently asked questions</h2>
            <div className="mt-4">
              <FaqAccordion faqs={p.faqs} />
            </div>
          </section>
        )}

        {p.references && p.references.length > 0 && (
          <section className="mt-10">
            <h2 className="font-display text-lg font-bold text-primary">References</h2>
            <ul className="mt-3 space-y-1 text-xs text-muted-foreground">
              {p.references.map((r) => (
                <li key={r}>{r}</li>
              ))}
            </ul>
          </section>
        )}

        <p className="mt-8 rounded-2xl bg-muted/40 p-4 text-xs text-muted-foreground">
          Medical disclaimer: this article is general education and does not replace a personal assessment. If your pain
          is severe, worsening, or accompanied by numbness, weakness or loss of bladder or bowel control, seek care
          promptly.
        </p>

        <div className="mt-8 rounded-2xl border border-border bg-secondary/40 p-6">
          <h2 className="font-display text-lg font-bold text-primary">Book with N S Physiotherapy</h2>
          <p className="mt-2 text-sm text-foreground/75">
            In clinic in Eco Park, Centurion, or home visits across Centurion and Midrand.
          </p>
          <div className="mt-4 flex flex-wrap gap-3">
            <Link to="/booking" className="btn-hero inline-flex h-11 items-center gap-2 rounded-full px-6 font-semibold">
              <Calendar className="h-4 w-4" /> Book online
            </Link>
            <a
              href={wa}
              target="_blank"
              rel="noreferrer"
              className="inline-flex h-11 items-center gap-2 rounded-full border border-border bg-background px-6 font-semibold text-primary hover:bg-accent"
            >
              <MessageCircle className="h-4 w-4" /> WhatsApp
            </a>
          </div>
        </div>

        {related.length > 0 && (
          <section className="mt-12">
            <h2 className="font-display text-xl font-bold text-primary">Related articles</h2>
            <ul className="mt-4 grid gap-3 sm:grid-cols-3">
              {related.map((r) => (
                <li key={r.slug}>
                  <ArticleCard post={r} />
                </li>
              ))}
            </ul>
          </section>
        )}
      </article>
    </PageShell>
  );
}
